import React, { useContext, useRef, useState, useCallback, useEffect } from 'react';
import { Col, Collapse, Form, Modal, Row, Spinner } from 'react-bootstrap';
import Card from 'react-bootstrap/Card';
import { MdArrowBack } from 'react-icons/md';
import { TiTicket } from 'react-icons/ti';
import { FaTrashAlt, FaWheelchair } from 'react-icons/fa';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import stripePaymentModthod from '../assets/images/paymentMethods.png';
import {
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApplePay from './ApplePay';
import buildTicketFees from '../helpers/buildTicketFees';
import usdFormatter from '../helpers/usdFormatter';
import getDiscountedPrice from '../helpers/getDiscountedPrice';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { AffContext } from '../contexts/AffiliateContext';
import { Post } from '../api/server';
import Button from './Button';
import PromoCodeInput from './PromoCodeInput';
import moment from 'moment';
import SeatingChartDisplay from './SeatingChartDisplay';
import RsvpCheckoutCallout from './RsvpCheckoutCallout';
import { ga4ItemsFromSeatingLines } from '../helpers/ga4EcommerceItems';
import { validateCheckoutBilling } from '../helpers/validateCheckoutBilling';
import { pushBeginCheckout, pushPurchase, resolveGa4TransactionId } from '../util/gtmEcommerce';

/** Backend user_type for RSVP / Dinner & A Show list signup */
const RSVP_DINNER_USER_TYPE_ID = '69d6b3ace5d2223ebecda524';

function subscribeRsvpDinnerListSignup(companyId, { first_name, last_name, email, phone }) {
  if (!companyId) return;
  const em = String(email ?? '').trim();
  if (!em) return;
  Post('/users', {
    first_name: String(first_name ?? '').trim(),
    last_name: String(last_name ?? '').trim(),
    phone: String(phone ?? '').trim(),
    email: em,
    company: companyId,
    user_type: RSVP_DINNER_USER_TYPE_ID,
  }).fail((err) => {
    const msg = err?.responseJSON;
    toast.error(
      typeof msg === 'string'
        ? msg
        : 'Could not save your dinner RSVP signup.'
    );
  });
}
/** RSVP Dinner Package signup function end */

function SeatingTicketCart({ show, handleClose, currentTicket, company, ticketQuantities, setTicketQuantities, handleShow, event, affId, ticketsLoading = false }) {
  const { aff } = useContext(AffContext);
  const navigate = useNavigate();
  const modalContentRef = useRef(null);
  const [inputValues, setInputValues] = useState({});
  const stripe = useStripe();
  const elements = useElements();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [doseAgree, setDoseAgree] = useState(false);
  const beginCheckoutSentRef = useRef(false);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedSeatIds, setSelectedSeatIds] = useState([]);
  const [orderSummaryOpen, setOrderSummaryOpen] = useState(false);
  const [zoomOutToAreaTrigger, setZoomOutToAreaTrigger] = useState(0);
  const [expandedRowLabels, setExpandedRowLabels] = useState([]);
  const [promoState, setPromoState] = useState(null);
  const [effectiveAffId, setEffectiveAffId] = useState(affId);
  const [rsvpDinnerOptIn, setRsvpDinnerOptIn] = useState(false);

  // Extract seats & blueprint — seats can live at currentTicket.seats (preferred) or under seatingChart
  const seats = currentTicket?.seats ?? currentTicket?.seatingChart?.seats ?? [];
  const blueprint = currentTicket?.seatingChart?.blueprint ?? currentTicket?.blueprint ?? null;
  const backgroundImageUrl = currentTicket?.seatingChart?.backgroundImage?.url ?? currentTicket?.backgroundImage?.url ?? '';

  // console.log('TicketCart render');

  useEffect(() => {
    // console.log('currentTicket:', currentTicket?.seatingChart);
  }, [currentTicket?.seatingChart]);


  // Helper: find area + section by sectionId (same idea as findSectionInBlueprint in SeatingChartDisplay)
function findSectionInBlueprint(blueprint, sectionId) {
  if (!sectionId || !blueprint?.areas) return null;
  for (const area of blueprint.areas) {
    const section = area?.sections?.find((s) => String(s.id) === String(sectionId));
    if (section) return { area, section };
  }
  return null;
}

const selectedLookup = selectedSectionId ? findSectionInBlueprint(blueprint, selectedSectionId) : null;
const selectedArea = selectedLookup?.area ?? null;
const selectedSection = selectedLookup?.section ?? null;

  const didSilentAffCheck = useRef(false);

// Keep effectiveAffId in sync with the prop unless promo overrides it
  useEffect(() => {
    if (!promoState?.affiliateId) {
      setEffectiveAffId(affId);
    }
  }, [affId, promoState]);

  // Silent affiliate promo code check
  useEffect(() => {
    if (!aff || !currentTicket?._id || didSilentAffCheck.current || promoState) return;
    didSilentAffCheck.current = true;

    Post('/promocodes/validate-affiliate', {
      slug: aff,
      eventId: currentTicket._id,
    }).then((response) => {
      if (response?.found) {
        const affPromo = {
          promoCodeId: response.promoCodeId,
          promoCode: response.code,
          discount_type: response.discount_type,
          discount_value: response.discount_value,
          applies_to: response.applies_to,
          price_tiers: response.price_tiers || [],
          ticket_inventories: response.ticket_inventories || [],
          affiliateId: response.affiliateId || null,
          affiliateName: response.affiliateName || null,
          max_per_user: response.max_per_user,
        };
        setPromoState(affPromo);
        if (affPromo.affiliateId) {
          setEffectiveAffId(affPromo.affiliateId);
        }
      }
    }).fail(() => {
      // Silent — do nothing on failure
    });
  }, [aff, currentTicket?._id]);

  const handlePromoApplied = (promo) => {
    setPromoState(promo);
    if (promo.affiliateId) {
      setEffectiveAffId(promo.affiliateId);
    }
    toast.success(`${promo.promoCode} applied — ${promo.discount_type === 'percent' ? promo.discount_value + '% off' : '$' + promo.discount_value.toFixed(2) + ' off'}!`);
  };

  const handlePromoRemoved = () => {
    setPromoState(null);
    setEffectiveAffId(affId);
  };

  function getSeatedDiscountedPrice(originalPrice, priceTier, sectionId) {
    return getDiscountedPrice(originalPrice, promoState, 'seated', priceTier, null, sectionId);
  }

  /** All-in price for a single ticket (face + fees/tax), matching checkout totals. */
  function getAllInTicketPrice(price, extraFee = 0, tax_rates = []) {
    if (!price || price <= 0) return 0;
    const feeResult = buildTicketFees(
      [{ price, extraFee: extraFee ?? 0, tax_rates: tax_rates ?? [] }],
      company?.application_fee,
      0.029,
      0.3
    );
    return feeResult.total ?? price;
  }

// Reset state when modal is closed - for when user closes the modal and opens it again
  useEffect(() => {
    if (!show) {
      setStep(1);
      setSelectedAreaId(null);
      setSelectedSectionId(null);
      setSelectedSeatIds([]);
      setInputValues({});
      setDoseAgree(false);
      setRsvpDinnerOptIn(false);
      setOrderSummaryOpen(false);
      setExpandedRowLabels([]);
    }
  }, [show]);

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo(0, 0);
    }
  };

  const handleAgree = () => {
    setDoseAgree(!doseAgree);
  }

  const handleNext = () => {
    // scroll to top
    if (doseAgree === false) return toast.warning('You have NOT been charged! Please agree to the Terms and Conditions before proceeding');
    scrollToTop();
    setStep(step + 1);
  };

  const handlePrevious = () => {
    setStep(step - 1);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  }

  const MAX_SEATS = 50;

  const toggleSeatSelection = (seatKey) => {
    // Only allow toggling available seats
    const seat = seats.find((s) => s.seatKey === seatKey);
    if (!seat || seat.status !== 'available') return;
    setSelectedSeatIds((prev) => {
      if (prev.includes(seatKey)) return prev.filter((id) => id !== seatKey);
      if (prev.length >= MAX_SEATS) {
        toast.warning(`You can only select up to ${MAX_SEATS} seats per order.`);
        return prev;
      }
      return [...prev, seatKey];
    });
  };

  const onAreaSelect = useCallback(() => {}, []);
  const onSectionSelect = useCallback((section) => {
    setSelectedSectionId(section?.id ?? null);
  }, []);

  const toggleRowExpanded = (rowLabel) => {
    setExpandedRowLabels((prev) =>
      prev.includes(rowLabel) ? prev.filter((r) => r !== rowLabel) : [...prev, rowLabel]
    );
  };

  // Reset expanded rows when section changes
  useEffect(() => {
    setExpandedRowLabels([]);
  }, [selectedSectionId]);

  // Resolve seatKey to area name using the flat seats array
  function getAreaNameForSeatId(seatKey) {
    const seat = seats.find((s) => s.seatKey === seatKey);
    return seat?.areaName ?? null;
  }

  // Get display label and price for a seatKey using the flat seats array
  function getSeatDisplayInfo(seatKey) {
    const seat = seats.find((s) => s.seatKey === seatKey);
    if (!seat) return { displayName: seatKey, price: 0 };
    const displayName = `Section ${seat.sectionName} · Row ${seat.rowLabel} · Seat ${seat.seatNumber}`;
    return { displayName, price: seat.price ?? 0 };
  }

  const removeSeatFromOrder = (seatId) => {
    setSelectedSeatIds((prev) => prev.filter((id) => id !== seatId));
  };

  // Sync selected seats to ticketQuantities so Order Summary and totals update
  React.useEffect(() => {
    if (!selectedSeatIds?.length) {
      setTicketQuantities({});
      setOrderSummaryOpen(false);
      return;
    }
    const next = {};
    selectedSeatIds.forEach((id) => {
      const areaName = getAreaNameForSeatId(id);
      if (areaName) next[areaName] = (next[areaName] || 0) + 1;
    });
    setTicketQuantities(next);
  }, [selectedSeatIds]);

  // Build list of available tickets for a section from the flat seats array
  function buildSectionTickets(sectionId) {
    return seats
      .filter((s) => s.sectionId === sectionId && s.status === 'available')
      .map((s) => ({
        id: s.seatKey,
        name: `Section ${s.sectionName} · Row ${s.rowLabel} · Seat ${s.seatNumber}`,
        seatLabel: `${s.rowLabel} - ${s.seatNumber}`,
        row: s.rowLabel,
        seatNumber: s.seatNumber,
        sectionName: s.sectionName,
        sectionId: s.sectionId,
        priceTier: s.priceTier,
        price: s.price ?? 0,
        attributes: s.attributes ?? [],
        isHandicap: s.isHandicap ?? false,
        extraFee: s.extraFee ?? 0,
        tax_rates: s.tax_rates ?? [],
      }));
  }

  const calculateTotalPrice = () => {
    let events = [];
    selectedSeatIds.forEach((seatKey) => {
      const seat = seats.find((s) => s.seatKey === seatKey);
      if (seat) {
        const originalPrice = seat.price ?? 0;
        const discounted = getSeatedDiscountedPrice(originalPrice, seat.priceTier, seat.sectionId);
        events.push({
          ticketName: `Section ${seat.sectionName} · Row ${seat.rowLabel} · Seat ${seat.seatNumber}`,
          price: discounted,
          originalPrice: originalPrice,
          extraFee: seat.extraFee || 0,
          tax_rates: seat.tax_rates || [],
        });
      }
    });
    return { events };
  };


  const { events } = calculateTotalPrice();
  const doseCost = events.reduce((acc, event) => acc + event.price, 0) > 0;

  const finalAmounts = buildTicketFees(events, company?.application_fee, 0.029, 0.3);
  // console.log('finalAmounts', finalAmounts.breakdown);
  const finalAmount = finalAmounts.total;

  const emitBeginCheckout = useCallback(() => {
    const items = ga4ItemsFromSeatingLines(selectedSeatIds, events);
    if (!items.length) return;
    pushBeginCheckout({
      currency: "USD",
      value: finalAmount,
      items,
      context: {
        event_listing_id: currentTicket?._id,
        event_name: event?.name,
      },
    });
  }, [finalAmount, selectedSeatIds, events, currentTicket?._id, event?.name]);

  useEffect(() => {
    if (!show) {
      beginCheckoutSentRef.current = false;
      return;
    }
    if (step === 1) {
      beginCheckoutSentRef.current = false;
      return;
    }
    if (step !== 2 || !doseCost) return;
    if (beginCheckoutSentRef.current) return;
    const items = ga4ItemsFromSeatingLines(selectedSeatIds, events);
    if (!items.length) return;
    beginCheckoutSentRef.current = true;
    emitBeginCheckout();
  }, [show, step, doseCost, selectedSeatIds, events, emitBeginCheckout]);

  const cardElementStyle = {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      lineHeight: '25px',
    },
    invalid: {
      color: '#9e2146',
    },
  };

  const payBill = async (e) => {
    e.preventDefault();
    const billingError = validateCheckoutBilling({
      terms: doseAgree,
      firstName: inputValues.first_name,
      lastName: inputValues.last_name,
      phone: inputValues.phone,
      email: inputValues.email,
    });
    if (billingError) return toast.warning(billingError);

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      return;
    }

    setIsLoading(false); // TODO: remove this

    /** On purchase success, subscribe to RSVP Dinner Package list if opted in */
    const onPurchaseSuccess = (response) => {
      const items = ga4ItemsFromSeatingLines(selectedSeatIds, events);
      const tid = resolveGa4TransactionId(response);
      if (tid && items.length) {
        pushPurchase({
          currency: "USD",
          value: finalAmount,
          transaction_id: tid,
          items,
          context: {
            event_listing_id: currentTicket?._id,
            event_name: event?.name,
          },
        });
      }
      Swal.close();
      setIsLoading(false);
      if (rsvpDinnerOptIn && company?._id) {
        subscribeRsvpDinnerListSignup(company._id, inputValues);
      }
      toast.success('Ticket(s) has been purchased successfully');
      navigate(`/events/confirmation/${response.user}/${response.event}`);
      handleClose();
    };

    if (doseCost) {
      const cardElement = elements.getElement(CardElement);

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: `${inputValues.first_name} ${inputValues.last_name}`,
          email: inputValues.email,
          phone: inputValues.phone,
        },
      });

      if (error) {
        setIsLoading(false);
        console.log('[error]', error);
        toast.error(error.message);
        return;
      }

      const purchasePayload = {
        token: doseCost ? paymentMethod : null,
        company: company._id,
        first_name: inputValues.first_name,
        last_name: inputValues.last_name,
        email: inputValues.email,
        phone: inputValues.phone,
        tickets: selectedSeatIds.length > 0 ? selectedSeatIds : ticketQuantities,
        selectedSeats: selectedSeatIds,
        amountDue: finalAmount,
        affId: effectiveAffId,
        promoCode: promoState?.promoCode || null,
        promoCodeId: promoState?.promoCodeId || null,
      };

      Swal.fire({
        title: "Processing Transaction ",
        html: "Please wait while we process your payment",
        background: "black",
        color: "white",
        allowEscapeKey: false,
        allowEnterKey: false,
        allowOutsideClick: () => !Swal.isLoading(),
        didOpen: () => {
          Swal.showLoading();
          Post(`/events/v3/buyTickets/${currentTicket._id}`, purchasePayload).then((response) => {
            onPurchaseSuccess(response);
          }).fail((error) => {
            console.log(error)
            Swal.close();
            setIsLoading(false);
            const errMsg = error?.responseJSON?.message
              || error?.responseJSON?.payment_intent?.last_payment_error?.message
              || 'Something went wrong on our end. Ticket(s) has not been purchased. Please try again or contact support.';
            toast.error(errMsg);
          });
        },
      });

    } else {
      const freePayload = {
        token: null,
        company: company._id,
        first_name: inputValues.first_name,
        last_name: inputValues.last_name,
        email: inputValues.email,
        tickets: selectedSeatIds.length > 0 ? selectedSeatIds : ticketQuantities,
        selectedSeats: selectedSeatIds,
        amountDue: finalAmount,
        affId: effectiveAffId,
        promoCode: promoState?.promoCode || null,
        promoCodeId: promoState?.promoCodeId || null,
      };

      Swal.fire({
        title: "Processing Transaction ",
        html: "Please wait while we process this free transaction",
        background: "black",
        color: "white",
        allowEscapeKey: false,
        allowEnterKey: false,
        allowOutsideClick: () => !Swal.isLoading(),
        didOpen: () => {
          Swal.showLoading();
          Post(`/events/v3/buyTickets/${currentTicket._id}`, freePayload).then((response) => {
            onPurchaseSuccess(response);
          }).fail((error) => {
            console.log(error)
            Swal.close();
            setIsLoading(false);
            const errMsg = error?.responseJSON?.message
              || error?.responseJSON?.payment_intent?.last_payment_error?.message
              || 'Something went wrong on our end. Ticket(s) has not been purchased. Please try again or contact support.';
            toast.error(errMsg);
          });
        },
      });
    }
  }

  const totalSavings = promoState ? events.reduce((acc, e) => acc + ((e.originalPrice ?? e.price) - e.price), 0) : 0;

  const Totals = () => {

    return (
      <div className="">
        <div className="card-body">
          {step == 2 && (
            <>
            <p className="small mb-2">Order Summary</p>
            <ul className="list-group list-group-flush">
              {selectedSeatIds.length > 0 ? (
                selectedSeatIds.map((seatId) => {
                  const { displayName, price } = getSeatDisplayInfo(seatId);
                  const seatObj = seats.find((s) => s.seatKey === seatId);
                  const discounted = getSeatedDiscountedPrice(price, seatObj?.priceTier, seatObj?.sectionId);
                  const hasDiscount = promoState && discounted < price;
                  return (
                    <li
                      key={seatId}
                      className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 pb-2 text-black"
                    >
                      <span className="text-break pe-2 small">{displayName}</span>
                      <span className="small d-flex align-items-center gap-1">
                        {hasDiscount && (
                          <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.75rem' }}>{usdFormatter(price)}</span>
                        )}
                        <span style={hasDiscount ? { color: '#dc3545', fontWeight: 600 } : {}}>{usdFormatter(discounted)}</span>
                      </span>
                    </li>
                  );
                })
              ) : (
                Object.keys(ticketQuantities || {}).map((ticketName, index) => {
                  const quantity = ticketQuantities[ticketName] || 0;
                  if (quantity > 0) {
                    const ticket = seats.find((t) => t.areaName === ticketName);
                    const price = ticket?.price || 0;
                    const totalPrice = quantity * price;
                    const displayName = ticket
                      ? `Section ${ticket.sectionName} · ${ticketName}`
                      : ticketName;
                    return (
                      <li
                        key={index}
                        className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 pb-2 text-black"
                      >
                        <span className="text-break pe-2 small">{quantity}x {displayName}</span>
                        <span className="small">{usdFormatter(totalPrice)}</span>
                      </li>
                    );
                  }
                  return null;
                })
              )}
            </ul>

            {promoState && totalSavings > 0 && (
              <div className="d-flex align-items-center justify-content-between rounded px-2 py-1 mb-1" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                <span className="small fw-semibold" style={{ color: '#856404' }}>Promo Discount ({promoState.promoCode})</span>
                <div className="d-flex align-items-center gap-2">
                  <span className="small fw-bold" style={{ color: '#dc3545' }}>-{usdFormatter(totalSavings)}</span>
                  <button
                    type="button"
                    className="btn btn-link p-0"
                    onClick={handlePromoRemoved}
                    style={{ fontSize: '0.7rem', color: '#856404', textDecoration: 'underline' }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            <div className="mt-3 mb-1">
              <PromoCodeInput
                eventId={currentTicket?._id}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                externalPromo={promoState}
              />
            </div>

            <hr className="my-3" />
  
            <div className="d-flex justify-content-between">
              <p className="mb-2">Subtotal</p>
              <p className="mb-2">{usdFormatter(finalAmounts.breakdown.tickets)}</p>
            </div>

            <div className="d-flex justify-content-between">
              <p className="mb-2">Venue Fee</p>
              <p className="mb-2">
                {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.extraTicketFees) : '$0.00'}
              </p>
            </div>

            <div className="d-flex justify-content-between">
              <p className="mb-2">Live Entertainment Tax (9%)</p>
              <p className="mb-2">
                {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.tax) : '$0.00'}
              </p>
            </div>
  
            <div className="d-flex justify-content-between">
              <p className="mb-2">Service + Processing Fees</p>
              <p className="mb-2">
                {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.processingFees + (finalAmounts?.breakdown?.serviceCharges - finalAmounts?.breakdown?.extraTicketFees)) : '$0.00'}
              </p>
            </div>
  
            <div className="d-flex justify-content-between mb-4">
              <p className="mb-2 fw-bold">Total Due</p>
              <p className="mb-2 fw-bold">{finalAmount > 0.31 ? usdFormatter(finalAmounts.total) : '$0.00'}</p>
            </div>
            </>
          )}
          
          {step === 1 && Object.values(ticketQuantities || {}).some(quantity => quantity > 0) && (
            <div style={{ marginTop: 50, zIndex: '9999' }} className='event-pay-buttons totals-strip-wrapper rounded-t-3xl'>
              <Collapse in={orderSummaryOpen}>
                <div id="order-summary-details" aria-labelledby="order-summary-toggle" className="overflow-y-auto max-h-[500px] pr-2">
                  <ul className="list-group list-group-flush pt-2">
                    {selectedSeatIds.length > 0 ? (
                      selectedSeatIds.map((seatId) => {
                        const { displayName, price } = getSeatDisplayInfo(seatId);
                        const seatObj = seats.find((s) => s.seatKey === seatId);
                        const discounted = getSeatedDiscountedPrice(price, seatObj?.priceTier, seatObj?.sectionId);
                        const hasDiscount = promoState && discounted < price;
                        return (
                          <li
                            key={seatId}
                            className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 pb-2 text-black"
                          >
                            <span className="text-break pe-2 text-sm">{displayName}</span>
                            <div className="d-flex align-items-center gap-2 flex-shrink-0">
                              <span className="text-sm d-flex align-items-center gap-1">
                                {hasDiscount && (
                                  <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.7rem' }}>{usdFormatter(price)}</span>
                                )}
                                <span style={hasDiscount ? { color: '#dc3545', fontWeight: 600 } : {}}>{usdFormatter(discounted)}</span>
                              </span>
                              {step === 1 && (
                                <button
                                  type="button"
                                  className="btn btn-link p-0 text-muted"
                                  onClick={(e) => { e.preventDefault(); removeSeatFromOrder(seatId); }}
                                  aria-label="Remove seat"
                                  title="Remove seat"
                                >
                                  <FaTrashAlt size={14} className="text-red-500"/>
                                </button>
                              )}
                            </div>
                          </li>
                        );
                      })
                    ) : (
                      Object.keys(ticketQuantities).map((ticketName, index) => {
                        const quantity = ticketQuantities[ticketName] || 0;
                        if (quantity > 0) {
                          const ticket = seats.find(
                            (t) => t.areaName === ticketName
                          );
                          const price = ticket?.price || 0;
                          const totalPrice = quantity * price;
                          return (
                            <li
                              key={index}
                              className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 pb-0 text-black"
                            >
                              {quantity}x {ticketName}
                              <span className="pull-right">{usdFormatter(totalPrice)}</span>
                            </li>
                          );
                        }
                        return null;
                      })
                    )}
                  </ul>

                  {promoState && totalSavings > 0 && (
                    <div className="d-flex align-items-center justify-content-between rounded px-2 py-1 mt-1" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
                      <span className="small fw-semibold" style={{ color: '#856404' }}>Promo ({promoState.promoCode})</span>
                      <span className="small fw-bold" style={{ color: '#dc3545' }}>-{usdFormatter(totalSavings)}</span>
                    </div>
                  )}

                  <hr className="mb-3 mt-2" />

                  <div className="d-flex justify-content-between">
                    <p className="mb-2">Subtotal</p>
                    <p className="mb-2">{usdFormatter(finalAmounts.breakdown.tickets)}</p>
                  </div>

                  <div className="d-flex justify-content-between">
                    <p className="mb-2">Venue Fee</p>
                    <p className="mb-2">
                      {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.extraTicketFees) : '$0.00'}
                    </p>
                  </div>

                  <div className="d-flex justify-content-between">
                    <p className="mb-2">Live Entertainment Tax (9%)</p>
                    <p className="mb-2">
                      {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.tax) : '$0.00'}
                    </p>
                  </div>

                  <div className="d-flex justify-content-between">
                    <p className="mb-2">Service + Processing Fees</p>
                    <p className="mb-2">
                      {finalAmount > 0.31 ? usdFormatter(finalAmounts.breakdown.processingFees + (finalAmounts?.breakdown?.serviceCharges - finalAmounts?.breakdown?.extraTicketFees)) : '$0.00'}
                    </p>
                  </div>

                  <div className="d-flex justify-content-between mb-2">
                    <p className="mb-2 fw-bold">Total Due</p>
                    <p className="mb-2 fw-bold">{finalAmount > 0.31 ? usdFormatter(finalAmounts.total) : '$0.00'}</p>
                  </div>
                </div>
              </Collapse>
              <button
                type="button"
                onClick={() => setOrderSummaryOpen((prev) => !prev)}
                aria-expanded={orderSummaryOpen}
                aria-controls="order-summary-details"
                id="order-summary-toggle"
                className="d-flex align-items-center justify-content-between w-100 border-0 bg-transparent p-0 pt-2 text-start text-black text-decoration-none"
              >
                <span className="small fw-semibold">Order Summary</span>
                <span className="d-flex align-items-center gap-2 small">
                  <span className="text-muted underline">Review Cart</span>
                  <MdExpandMore
                    size={20}
                    className="text-muted transition-transform"
                    style={{ transform: !orderSummaryOpen ? 'rotate(180deg)' : 'none' }}
                    aria-hidden
                  />
                </span>
              </button>

              <div className='flex items-center gap-2' style={{ marginBottom: 4 }}>
                <div className="terms-switch-lg flex-shrink-0">
                  <Form.Check // prettier-ignore
                    type="switch"
                    required
                    checked={doseAgree}
                    onChange={handleAgree}
                  />
                </div>
                <span className='agree-switch-text'> I agree to the <Link to='/terms' target='_blank'>Terms and Conditions</Link></span>
              </div>
              <Button
                style={{ width: '100%', marginBottom: 10, padding: "8px 0" }}
                text={doseCost ? usdFormatter(finalAmount) + ' Checkout' : '$0.00 Checkout'}
                black={true}
                outline={true}
                onClick={handleNext}
              />
            </div>
          )}
          {step === 2 && (
            <Row>
              <Col xs={12}>
                {isLoading ? (
                  <Button text={isLoading ? 'Processing...' : 'Pay'} outline={true} black={true} style={{width: '100%'}} disabled={isLoading} className="btn btn-block w-full mt-1" />
                ) : (
                  <Button text={isLoading ? 'Processing...' : 'Pay'} onClick={payBill} outline={true} black={true} style={{width: '100%'}} disabled={isLoading} className="btn btn-block w-full mt-1" />
                )}



              </Col>
            </Row>
          )}
        </div>
      </div>
    )
  }

  const cartScreenContent = (
    <Row id="ticket-cart">
      <Col lg={8} className="seating-chart-container">
        <div className="d-flex justify-content-between align-items-center mb-2 px-3">
          <span className="textxs mb-0">
            <a
              href="#!"
              onClick={handleClose}
              className="text-body d-inline-flex align-items-center"
            >
              <MdArrowBack /> {event?.name || 'Go Back'}
              {/* <MdArrowBack /> Go Back */}
            </a>
          </span>
          <div className="text-end">
            {/* {event?.name && (
              <div className="fw-semibold text-black small">
                {event.name}
              </div>
            )} */}
            <p className="mb-0 text-xs">
              {/* Available Tickets ( */}
              {event
                ? moment(event.start_date_local || event.start_date_display).format('M/D/YY')
                : ''}
              , {event?.start_time_display} {event?.timezone_abbr}
              {/* ) */}
            </p>
          </div>
        </div>
        {/* <hr /> */}
        <div className="d-flex justify-content-between align-items-center seating-chart-wrapper border-y-2 border-gray-100" >
          <SeatingChartDisplay
            blueprint={blueprint}
            seats={seats}
            selectedAreaId={null}
            selectedSectionId={selectedSectionId}
            selectedSeatIds={selectedSeatIds}
            onAreaSelect={onAreaSelect}
            onSectionSelect={onSectionSelect}
            onSeatToggle={toggleSeatSelection}
            imageUrl={backgroundImageUrl}
            zoomOutToAreaTrigger={zoomOutToAreaTrigger}
            onZoomOutToSections={() => {
              setSelectedSectionId(null);
              setZoomOutToAreaTrigger((t) => t + 1);
            }}
          />
        </div>
      </Col>
      <Col lg={4} className="d-flex flex-column text-sm px-4">
        <div className="mb-2 flex-shrink-0 px-2">
          <div className="flex-shrink-0 px-2 text-center">
            {selectedSectionId && selectedSection && selectedArea ? (
              <>
                <div className="font-semibold">
                  {selectedArea.name} · Section {selectedSection.name}
                </div>
              </>
            ) : (
              <div className="choose-section-hint text-muted small d-flex align-items-center justify-content-center justify-content-md-start gap-2">
                <span className="choose-section-hint__arrow choose-section-hint__arrow--left d-none d-md-inline-flex" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
                </span>
                <span className="choose-section-hint__arrow choose-section-hint__arrow--up choose-section-hint__arrow--mobile d-inline-flex d-md-none" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                </span>
                <span>Tap seats on the map to select tickets</span>
                <span className="choose-section-hint__arrow choose-section-hint__arrow--up choose-section-hint__arrow--mobile d-inline-flex d-md-none" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7"/></svg>
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="ticket-list-scroll flex-grow-1 flex-shrink-1 min-h-0 mb-3">
        {selectedSectionId && selectedSection ? (
          <>
          {(() => {
            const sectionTickets = buildSectionTickets(selectedSectionId);
            const ticketsByRow = sectionTickets.reduce((acc, t) => {
            const row = t.row ?? 'General';
            if (!acc[row]) acc[row] = [];
            acc[row].push(t);
            return acc;
          }, {});
          const rowLabels = Object.keys(ticketsByRow).sort((a, b) => {
            const aNum = Number(a);
            const bNum = Number(b);
            if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
            if (!Number.isNaN(aNum)) return 1;
            if (!Number.isNaN(bNum)) return -1;
            return String(a).localeCompare(String(b));
          });
          return (
            <>
              <Row>
                {rowLabels.map((rowLabel) => {
                  const rowTickets = ticketsByRow[rowLabel];
                  const fallbackFace = selectedSection.price ?? selectedArea?.price ?? 0;
                  const rowAllInRaw = rowTickets.map((t) => {
                    const p = t.price ?? fallbackFace;
                    return getAllInTicketPrice(p, t.extraFee, t.tax_rates);
                  }).filter((p) => p > 0);
                  const rowAllInDiscounted = rowTickets.map((t) => {
                    const p = t.price ?? fallbackFace;
                    const discounted = getSeatedDiscountedPrice(p, t.priceTier, t.sectionId);
                    return getAllInTicketPrice(discounted, t.extraFee, t.tax_rates);
                  }).filter((p) => p > 0);
                  const fallbackAllIn = getAllInTicketPrice(fallbackFace);
                  const minPrice = rowAllInDiscounted.length ? Math.min(...rowAllInDiscounted) : fallbackAllIn;
                  const maxPrice = rowAllInDiscounted.length ? Math.max(...rowAllInDiscounted) : fallbackAllIn;
                  const origMin = rowAllInRaw.length ? Math.min(...rowAllInRaw) : 0;
                  const origMax = rowAllInRaw.length ? Math.max(...rowAllInRaw) : 0;
                  const rowHasDiscount = promoState && (minPrice < origMin || maxPrice < origMax);
                  const priceDisplay = minPrice === maxPrice ? usdFormatter(minPrice) : `${usdFormatter(minPrice)} – ${usdFormatter(maxPrice)}`;
                  const isExpanded = expandedRowLabels.includes(rowLabel);
                  const selectedInRow = rowTickets.filter((t) => selectedSeatIds.includes(t.id)).length;
                  return (
                    <Col key={rowLabel} xs={12} className="mb-2">
                      <Card
                        style={{ borderLeft: `4px solid ${selectedSection.color}`, overflow: 'visible' }}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className="cursor-pointer d-flex align-items-center justify-content-between py-1 px-3"
                          onClick={() => toggleRowExpanded(rowLabel)}
                          onKeyDown={(e) => e.key === 'Enter' && toggleRowExpanded(rowLabel)}
                          style={{
                            ...(isExpanded && {
                              position: 'sticky',
                              top: 0,
                              zIndex: 10,
                              backgroundColor: 'var(--bs-gray-200)',
                              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            }),
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <TiTicket size={22} style={{ color: selectedSection.color, flexShrink: 0 }} />
                            <div className="flex flex-row justify-content-between gap-2">
                              <div className="text-black fw-medium">Row {rowLabel}</div>
                              <div className="text-sm text-muted">
                                {rowHasDiscount && (
                                  <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 4, fontSize: '0.75rem' }}>
                                    {origMin === origMax ? usdFormatter(origMin) : `${usdFormatter(origMin)} – ${usdFormatter(origMax)}`}
                                  </span>
                                )}
                                <span style={rowHasDiscount ? { color: '#dc3545', fontWeight: 600 } : {}}>
                                  {priceDisplay}
                                </span>
                                <span className="ms-1">all in price</span>
                                {selectedInRow > 0 && (
                                  <span className="text-success ms-1">· {selectedInRow} selected</span>
                                )}
                              </div>
                            </div>
                          </div>
                          {isExpanded ? (
                            <MdExpandLess size={24} className="text-muted" />
                          ) : (
                            <MdExpandMore size={24} className="text-muted" />
                          )}
                        </div>
                        <Collapse in={isExpanded}>
                          <div className="px-2 pb-3 pt-0">
                            <Row className="g-2 mt-1">
                              {rowTickets.map((ticket) => {
                                const isSelected = selectedSeatIds.includes(ticket.id);
                                const price = ticket.price ?? selectedSection.price ?? selectedArea?.price ?? 0;
                                const discounted = getSeatedDiscountedPrice(price, ticket.priceTier, ticket.sectionId);
                                const hasDiscount = promoState && discounted < price;
                                const priceAfterFees = getAllInTicketPrice(discounted, ticket.extraFee, ticket.tax_rates);
                                const origAllIn = getAllInTicketPrice(price, ticket.extraFee, ticket.tax_rates);
                                return (
                                  <Col key={ticket.id} xs={12}>
                                    <Card
                                      className="cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleSeatSelection(ticket.id);
                                      }}
                                      style={{
                                        borderLeft: `3px solid ${selectedSection.color}`,
                                        backgroundColor: isSelected ? 'var(--bs-light)' : undefined,
                                        borderColor: isSelected ? selectedSection.color : undefined,
                                      }}
                                    >
                                      <Card.Body className="d-flex align-items-center justify-content-between py-2">
                                        <div className="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
                                          <TiTicket size={18} style={{ color: selectedSection.color, flexShrink: 0 }} />
                                          <div>
                                            <div className="text-black fw-medium small d-flex align-items-center flex-wrap gap-1">
                                              <span>{ticket.seatLabel}</span>
                                              {hasDiscount && (
                                                <span style={{ backgroundColor: '#dc3545', color: '#fff', fontSize: '0.6rem', fontWeight: 700, borderRadius: 3, padding: '0px 4px', lineHeight: 1.4 }}>
                                                  {promoState.discount_type === 'percent' ? `${promoState.discount_value}% OFF` : `$${promoState.discount_value} OFF`}
                                                </span>
                                              )}
                                              {hasDiscount ? (
                                                <span>
                                                  <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.75rem' }}>{usdFormatter(origAllIn)}</span>
                                                  <span className="ms-1" style={{ color: '#dc3545', fontWeight: 600 }}>{usdFormatter(priceAfterFees)}</span>
                                                </span>
                                              ) : (
                                                <span className="font-light small">{usdFormatter(priceAfterFees)}</span>
                                              )}
                                              <span className="text-black-50">all in price</span>
                                            </div>
                                          
                                            <div className="flex flex-row justify-content-between gap-2">
                                            </div>
                                            {ticket.attributes?.length > 0 && (
                                              <div className="d-flex align-items-center gap-2 mt-1">
                                                {ticket.attributes.includes('ADA') && (
                                                  <span className="d-inline-flex align-items-center justify-content-center" style={{ backgroundColor: '#0d6efd', color: 'white', borderRadius: '4px', padding: '2px 6px' }}>
                                                    <FaWheelchair size={12} title="ADA Accessible" />
                                                  </span>
                                                )}
                                                {ticket.attributes.filter((attr) => attr !== 'ADA').length > 0 && (
                                                  <span className="badge bg-secondary small">{ticket.attributes.filter((attr) => attr !== 'ADA').join(', ')}</span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                        <div className="text-end flex-shrink-0 ms-2">
                                          <span className={isSelected ? 'text-success fw-medium small' : 'text-primary small'}>
                                            {isSelected ? 'Selected' : 'Select'}
                                          </span>
                                        </div>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          </div>
                        </Collapse>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )})()}
        </>
        ) : (
          <Row>
            <p className="mb-0 text-base fw-semibold text-start mt-3">Seat Color Guide</p>
            {blueprint?.areas?.map((area) => {
            const areaSeats = seats.filter((s) => s.areaId === area.id && s.status === 'available');
            const firstSeat = areaSeats[0];
            const affiliateSlug = firstSeat?.affiliate?.slug || null;
            if (affiliateSlug && aff !== affiliateSlug) return null;
            const fallbackAllIn = getAllInTicketPrice(area.price ?? 0);
            const origAllInPrices = areaSeats.map((s) =>
              getAllInTicketPrice(s.price ?? 0, s.extraFee, s.tax_rates)
            ).filter((p) => p > 0);
            const discAllInPrices = areaSeats.map((s) => {
              const discounted = getSeatedDiscountedPrice(s.price ?? 0, s.priceTier, s.sectionId);
              return getAllInTicketPrice(discounted, s.extraFee, s.tax_rates);
            }).filter((p) => p > 0);
            const origMinP = origAllInPrices.length ? Math.min(...origAllInPrices) : fallbackAllIn;
            const origMaxP = origAllInPrices.length ? Math.max(...origAllInPrices) : fallbackAllIn;
            const minPrice = discAllInPrices.length ? Math.min(...discAllInPrices) : fallbackAllIn;
            const maxPrice = discAllInPrices.length ? Math.max(...discAllInPrices) : fallbackAllIn;
            const areaHasDiscount = promoState && (minPrice < origMinP || maxPrice < origMaxP);
            const priceDisplay = minPrice === maxPrice
              ? usdFormatter(minPrice)
              : `${usdFormatter(minPrice)} – ${usdFormatter(maxPrice)}`;
            return (
              <Col className="ticketCard mb-2" key={area.id} xs={12}>
                <Card
                  className="h-100"
                  style={{ borderLeft: `4px solid ${area.color || '#333'}` }}
                >
                  <Card.Body className="d-flex align-items-center justify-content-between py-2">
                    <div className="d-flex align-items-center gap-3">
                      <TiTicket size={32} style={{ color: area.color || '#333', flexShrink: 0 }} />
                      <div>
                        <div className="text-black ticketCartTicketName text-[16px] d-flex align-items-center gap-1 flex-wrap">
                          {area.name}
                          {areaHasDiscount && (
                            <span style={{ backgroundColor: '#dc3545', color: '#fff', fontSize: '0.6rem', fontWeight: 700, borderRadius: 3, padding: '0px 4px', lineHeight: 1.4 }}>
                              {promoState.discount_type === 'percent' ? `${promoState.discount_value}% OFF` : `$${promoState.discount_value} OFF`}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="ticket-price text-end">
                      {areaHasDiscount && (
                        <span style={{ textDecoration: 'line-through', color: '#999', marginRight: 4, fontSize: '0.75rem' }}>
                          {origMinP === origMaxP ? usdFormatter(origMinP) : `${usdFormatter(origMinP)} – ${usdFormatter(origMaxP)}`}
                        </span>
                      )}
                      <span style={areaHasDiscount ? { color: '#dc3545', fontWeight: 600 } : {}}>
                        {priceDisplay}
                      </span>
                      <span className="d-block text-black-50 small" style={{ fontSize: '0.7rem', fontWeight: 400 }}>
                        all in price
                      </span>
                    </span>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
          </Row>
        )}
        </div>

        <div className="flex-shrink-0">
          <Totals />
        </div>
      </Col>
    </Row>
  );

  const hasAvailableTickets =
    Array.isArray(event?.seatingChart?.seats) &&
    event.seatingChart.seats.some((seat) => seat.status === 'available');

  const buttonClass = 'bg-white text-black border border-black px-[35px] w-full font-medium text-sm rounded-[5px] flex items-center justify-center gap-2 mt-2';

  return (
    <>
    {ticketsLoading ? (
        <div
          className={buttonClass}
          style={{ width: '100%', padding: '8px 0' }}
          aria-busy="true"
          aria-live="polite"
        >
          <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
        </div>
      ) : moment(event?.end_date_local).isAfter(moment()) ? (
        hasAvailableTickets ? (
          <Button
            text="Find Tickets"
            onClick={handleShow}
            variant="primary mt-2"
            black={false}
            outline={true}
            style={{ width: '100%', padding: '8px 0' }}
          />
        ) : (
          <Button
            disabled
            text="No Tickets Available"
            variant="primary mt-2"
            black={false}
            outline={true}
            style={{ width: '100%', padding: '8px 0' }}
          />
        )
      ) : (
        <Button disabled text="Event Ended" variant="primary mt-2" black={false} outline={true} style={{width: '100%', padding: '20px 0'}} />
      )}

      <Modal
        // dialogClassName="modal-95vw"
        fullscreen
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        size="xl"
      >
        <Modal.Body style={{ marginBottom: 0, padding: '10px 0px 0px 0px'}} ref={modalContentRef}>
          {step === 1 && cartScreenContent}
          {step === 2 && (
            <Row style={{ margin: 10 }} className='checkout-page'>
              <Col md={7}>
                <Row>
                  <Col md={12}>
                    <h5 className="mb-3">
                      <a href="#!" onClick={handlePrevious} className="text-body flex items-center">
                        <MdArrowBack /> Back to Cart
                      </a>
                    </h5>
                    <hr />
                    <RsvpCheckoutCallout
                      showCheckbox
                      optIn={rsvpDinnerOptIn}
                      onOptInChange={setRsvpDinnerOptIn}
                      disabled={isLoading}
                      optInId="rsvp-dinner-opt-in-seating"
                    />
                    <h3 className="text-black">Billing Information</h3>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="first_name">
                      <Form.Control className='customInput' disabled={isLoading} name="first_name" onChange={(e) => handleInputChange(e)} type="text" placeholder="First Name *" value={inputValues.first_name} />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="last_name">
                      <Form.Control disabled={isLoading} name="last_name" onChange={(e) => handleInputChange(e)} type="text" placeholder="Last Name *" value={inputValues.last_name} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="phone">
                      <Form.Control disabled={isLoading} name="phone" onChange={(e) => handleInputChange(e)} type="phone" placeholder="Phone Number *" value={inputValues.phone} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Control disabled={isLoading} name="email" onChange={(e) => handleInputChange(e)} type="email" placeholder="Email Address *" value={inputValues.email} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  {doseCost && (
                    <>
                      <Col md={12}>
                        <h3 className='text-black mt-20'>Payment Information</h3>

                        <ApplePay
                          terms={doseAgree}
                          ticketQuantities={ticketQuantities}
                          selectedSeatIds={selectedSeatIds}
                          totalAmountDue={finalAmount}
                          event={event}
                          handleClose={handleClose}
                          currentTicket={currentTicket}
                          history={navigate}
                          affId={effectiveAffId}
                          firstName={inputValues.first_name}
                          lastName={inputValues.last_name}
                          phone={inputValues.phone}
                          email={inputValues.email}
                          promoCode={promoState?.promoCode || null}
                          promoCodeId={promoState?.promoCodeId || null}
                        />

                        <div className="card-element-container">
                          <Form.Control
                            disabled={isLoading}
                            as={CardElement}
                            options={{ style: cardElementStyle }}
                          />
                        </div>
                      </Col>
                      <Col className='text-center items-center flex flex-col justify-center' md={12}>
                        <div className="flex justify-center">
                          <img className='margin-top-20' src={stripePaymentModthod} width={150} alt="methods" />
                        </div>
                        <div className="text-center">
                          <p>
                            <a href='https://stripe.com/legal/ssa' target='_blank' rel='noreferrer'>
                              Stripe Terms and Conditions
                            </a>
                          </p>
                        </div>
                      </Col>
                    </>
                  )}
                </Row>
              </Col>
              <Col md={5}>
                <Totals />
              </Col>
            </Row>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default SeatingTicketCart;